import { useEffect, useMemo, useState, type ComponentType, type SVGProps } from "react";
import { Skeleton } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CustomTabs, CustomTypography } from "sipli-fleet-core/components";
import { BACK_ICON } from "src/assets/svgs";
import { useQueryGetCustomerById } from "src/queries/customers.queries";
import type { Customer } from "src/services/customerService";
import CustomerBreadcrumb from "./CustomerBreadcrumb";
import CustomerSettingsTab from "./CustomerSettingsTab";
import DevicesTab from "./DevicesTab/DevicesTab";
import UsersTab from "./UsersTab/UsersTab";
import "../Customers.css";
import { renderTabLabel } from "../constant";

const BackIcon = BACK_ICON as unknown as ComponentType<SVGProps<SVGSVGElement>>;

const getCustomerName = (customer?: Customer | null) => {
  const fullName = [customer?.name, customer?.surname].filter(Boolean).join(" ");
  return customer?.companyName || fullName || "--";
};

const getCustomerDetailTab = (tab?: string | null) => {
  if (tab === "users") return "users";

  if (tab === "settings" || tab === "limits" || tab === "modules" || tab === "tachograph") {
    return "settings";
  }

  return "devices";
};

const getCustomerSettingsTab = (tab?: string | null) => {
  if (tab === "modules") return "modules";
  if (tab === "tachograph") return "tachograph";

  return "limits";
};

const CustomerDetail = () => {
  const { clientId } = useParams();
  const { state, search, pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: customerDetailData, isLoading: isCustomerLoading } =
    useQueryGetCustomerById(clientId);
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const requestedTab = queryParams.get("tab");
  const defaultTab = getCustomerDetailTab(requestedTab);
  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (!queryParams.get("tab")) {
      const params = new URLSearchParams(queryParams);
      params.set("tab", "devices");

      navigate(`${pathname}?${params.toString()}`, {
        replace: true,
        state,
      });
    }
  }, [navigate, pathname, queryParams, state]);

  const stateCustomer = (state as { customer?: Customer } | null)?.customer;
  const currentCustomer = customerDetailData?.data?.response?.client ??
    (stateCustomer && String(stateCustomer.id) === clientId ? stateCustomer : undefined);
  const customerName = getCustomerName(currentCustomer);

  const tabItems = useMemo(
    () => [
      {
        key: "devices",
        // label: t("devices"),
        label: renderTabLabel(null, t("devices"), activeTab === "devices"),
        children: (
          <DevicesTab clientId={clientId} currentCustomer={currentCustomer} />
        ),
      },
      {
        key: "users",
        // label: t("users"),
        label: renderTabLabel(null, t("users"), activeTab === "users"),
        children: <UsersTab clientId={clientId} />,
      },
      {
        key: "settings",
        label: renderTabLabel(null, t("settings"), activeTab === "settings"),
        children: (
          <CustomerSettingsTab
            clientId={clientId}
            defaultActiveKey={getCustomerSettingsTab(requestedTab)}
          />
        ),
      },
    ],
    [activeTab, clientId, requestedTab, currentCustomer, t]
  );

  const handleTabChange = (key: string) => {
    const nextTab = key === "users" || key === "settings" ? key : "devices";
    const params = new URLSearchParams(search);
    params.set("tab", nextTab);

    navigate(`${pathname}?${params.toString()}`, {
      replace: true,
      state,
    });

    setActiveTab(nextTab);
  };

  return (
    <section className="min-h-full py-6 bg-gray1">
      <div className="customer-detail-header">
        <div className="flex items-center gap-3">
          <BackIcon
            className="w-[16px] cursor-pointer"
            onClick={() => navigate("/customers", { state })}
            aria-label={t("back")}
          />
          <CustomTypography.CustomTitle level={3} className="customer-detail-title !m-0 !font-bold">
            {isCustomerLoading && !currentCustomer ? (
              <Skeleton.Input active size="small" />
            ) : customerName}
          </CustomTypography.CustomTitle>
        </div>
        <div className="min-w-0 col-start-2">
          <CustomerBreadcrumb
            ariaLabel={t("customer_details")}
            separator="/"
            items={[
              {
                label: t("customers"),
                path: "/customers",
              },
              {
                label: t("vehicle_list", { defaultValue: "Vehicle list" }),
              },
            ]}
          />
        </div>
      </div>

      <CustomTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        className="customer-detail-tabs"
        items={tabItems}
      />
    </section>
  );
};

export default CustomerDetail;
